import type { StudentSimulatorAppView } from "../../shared/domain/workspace.js";
import type { SimulatorAppDefinition, SimulatorProfileId } from "../contracts/appManifest.js";

const riskyNameReplacements: Array<[RegExp, string]> = [
  [/\bMicrosoft 365\b/gi, "Cloud"],
  [/\bMicrosoft\b/gi, "FirstDesk"],
  [/\bWindows Defender\b/gi, "Firewall"],
  [/\bWindows Server Backup\b/gi, "Server Backup"],
  [/\bWindows Server\b/gi, "FirstDesk Server"],
  [/\bWindows\b/gi, "FirstDesk"],
  [/\bAzure\b/gi, "Cloud"],
  [/\bEntra\b/gi, "Cloud Directory"],
  [/\bEdge\b/gi, "Browser"],
  [/\bStore\b/gi, "App Catalog"],
  [/\bActive Directory\b/gi, "Directory"]
];

export function sanitizeSimulatorAppTitle(value: string): string {
  return riskyNameReplacements.reduce(
    (title, [pattern, replacement]) => title.replace(pattern, replacement),
    value.trim()
  );
}

const sharedUtilityProfiles: SimulatorProfileId[] = ["workstation", "server-2025", "client-cloud-admin"];
const serverProfiles: SimulatorProfileId[] = ["server-2025", "server-core"];
const cloudProfiles: SimulatorProfileId[] = ["client-cloud-admin"];

export const baseSimulatorApps: SimulatorAppDefinition[] = [
  {
    id: "explorer",
    title: "FD Explorer",
    shortTitle: "Explorer",
    kind: "explorer",
    category: "productivity",
    icon: "folder",
    runAliases: ["explorer", "file explorer", "fd explorer"],
    keywords: ["files", "folders", "shares", "permissions"],
    profileIds: sharedUtilityProfiles,
    pinned: true,
    desktop: true,
    implemented: true,
    launchCapability: "file.folder.read",
    defaultSize: { width: 900, height: 610 },
    defaultPosition: { x: 72, y: 54 }
  },
  {
    id: "settingsSystem",
    title: "FD Settings",
    shortTitle: "Settings",
    kind: "settings",
    category: "desktop",
    icon: "settings",
    runAliases: ["settings", "ms-settings", "control", "system settings"],
    keywords: ["system", "about", "network", "display", "devices"],
    profileIds: sharedUtilityProfiles,
    pinned: true,
    desktop: true,
    implemented: true,
    defaultSize: { width: 920, height: 620 },
    defaultPosition: { x: 108, y: 66 }
  },
  {
    id: "cmd",
    title: "FD Terminal",
    shortTitle: "Terminal",
    kind: "terminal",
    category: "desktop",
    icon: "terminal",
    runAliases: ["cmd", "command", "terminal", "powershell", "pwsh", "fd terminal"],
    keywords: ["shell", "command", "script", "console"],
    profileIds: ["workstation", "server-2025", "server-core", "client-cloud-admin"],
    pinned: true,
    desktop: true,
    implemented: true,
    launchCapability: "terminal.command.write",
    defaultSize: { width: 860, height: 560 },
    defaultPosition: { x: 126, y: 86 }
  },
  {
    id: "lusrmgr",
    title: "Local Users and Groups",
    kind: "local-users-groups",
    category: "admin",
    icon: "users",
    runAliases: ["lusrmgr.msc", "lusrmgr", "local users", "local users and groups"],
    keywords: ["users", "groups", "local accounts", "membership"],
    profileIds: ["workstation", "server-2025"],
    pinned: true,
    desktop: true,
    implemented: true,
    launchCapability: "local.user.read",
    defaultSize: { width: 860, height: 590 },
    defaultPosition: { x: 148, y: 78 }
  },
  {
    id: "server-manager",
    title: "Server Manager-style Console",
    shortTitle: "Server Console",
    kind: "server-manager",
    category: "server",
    icon: "server",
    runAliases: ["servermanager", "server manager", "server-manager"],
    keywords: ["roles", "features", "domain controller", "server"],
    profileIds: serverProfiles,
    pinned: true,
    desktop: true,
    implemented: true,
    launchCapability: "serverRoles.read",
    defaultSize: { width: 980, height: 660 },
    defaultPosition: { x: 64, y: 42 }
  },
  {
    id: "notepad",
    title: "FD Notepad",
    kind: "notepad",
    category: "productivity",
    icon: "file-text",
    runAliases: ["notepad", "kladblok"],
    keywords: ["notes", "text", "file"],
    profileIds: sharedUtilityProfiles,
    pinned: true,
    desktop: false,
    defaultSize: { width: 760, height: 540 }
  },
  {
    id: "paint",
    title: "FD Paint",
    kind: "paint",
    category: "productivity",
    icon: "palette",
    runAliases: ["paint", "mspaint"],
    keywords: ["drawing", "image", "evidence"],
    profileIds: ["workstation", "client-cloud-admin"],
    desktop: false,
    defaultSize: { width: 860, height: 560 }
  },
  {
    id: "calc",
    title: "FD Calculator",
    kind: "calculator",
    category: "productivity",
    icon: "calculator",
    runAliases: ["calc", "calculator"],
    keywords: ["calculator", "utility"],
    profileIds: ["workstation", "client-cloud-admin"],
    defaultSize: { width: 460, height: 560 }
  },
  {
    id: "ncpa",
    title: "Network Connections",
    kind: "network-connections",
    category: "network",
    icon: "network",
    runAliases: ["ncpa.cpl", "network connections", "adapters"],
    keywords: ["ip", "dns", "adapter", "network"],
    profileIds: ["workstation", "server-2025"],
    desktop: true,
    launchCapability: "network.adapter.read",
    defaultSize: { width: 860, height: 560 }
  },
  {
    id: "sysdm",
    title: "System Properties",
    kind: "system-properties",
    category: "desktop",
    icon: "monitor",
    runAliases: ["sysdm.cpl", "system properties", "domain join", "hostname"],
    keywords: ["hostname", "domain", "computer name", "workgroup"],
    profileIds: ["workstation", "server-2025"],
    desktop: true,
    launchCapability: "system.identity.read",
    defaultSize: { width: 760, height: 520 }
  },
  {
    id: "remoteDesktop",
    title: "Remote Access",
    kind: "remote-access",
    category: "network",
    icon: "monitor",
    runAliases: ["mstsc", "mstsc.exe", "remote access", "remote desktop", "rdp"],
    keywords: ["remote", "access", "rdp", "connection"],
    profileIds: ["workstation", "server-2025"],
    desktop: false,
    implemented: true,
    launchCapability: "remoteDesktop.read",
    defaultSize: { width: 660, height: 520 }
  },
  {
    id: "controlPrinters",
    title: "Printers",
    kind: "printers",
    category: "productivity",
    icon: "printer",
    runAliases: ["control printers", "printers", "devices and printers", "print management"],
    keywords: ["printer", "driver", "port", "default"],
    profileIds: ["workstation", "server-2025", "client-cloud-admin"],
    desktop: false,
    implemented: true,
    launchCapability: "local.printer.read",
    defaultSize: { width: 900, height: 610 }
  },
  {
    id: "aduc",
    title: "Directory Users and Computers",
    kind: "directory-users-computers",
    category: "admin",
    icon: "directory",
    runAliases: ["dsa.msc", "aduc", "directory users", "users and computers"],
    keywords: ["directory", "users", "groups", "ou"],
    profileIds: serverProfiles,
    desktop: true,
    launchCapability: "domainUsers.read",
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "dns-manager",
    title: "DNS Manager",
    kind: "dns-manager",
    category: "server",
    icon: "dns",
    runAliases: ["dnsmgmt.msc", "dns", "dns manager", "dnsmanager"],
    keywords: ["zone", "record", "forwarder", "dns"],
    profileIds: serverProfiles,
    desktop: true,
    launchCapability: "dns.zone.read",
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "dhcpManager",
    title: "DHCP Manager",
    kind: "dhcp-manager",
    category: "server",
    icon: "dhcp",
    runAliases: ["dhcpmgmt.msc", "dhcp", "dhcp manager"],
    keywords: ["scope", "reservation", "option", "lease"],
    profileIds: serverProfiles,
    desktop: true,
    launchCapability: "dhcp.scope.read",
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "gpmc",
    title: "Policy Management",
    kind: "group-policy",
    category: "admin",
    icon: "policy",
    runAliases: ["gpmc.msc", "gpmc", "group policy", "policy management"],
    keywords: ["policy", "gpo", "link", "security filtering"],
    profileIds: serverProfiles,
    launchCapability: "policy.gpo.read",
    defaultSize: { width: 940, height: 620 }
  },
  {
    id: "services",
    title: "Services",
    kind: "services",
    category: "admin",
    icon: "services",
    runAliases: ["services.msc", "services"],
    keywords: ["service", "start", "stop", "startup"],
    profileIds: ["workstation", "server-2025", "server-core"],
    launchCapability: "service.read",
    defaultSize: { width: 880, height: 590 }
  },
  {
    id: "eventvwr",
    title: "Event Viewer",
    kind: "event-viewer",
    category: "admin",
    icon: "logs",
    runAliases: ["eventvwr.msc", "event viewer", "logs"],
    keywords: ["audit", "event", "log", "security"],
    profileIds: ["workstation", "server-2025"],
    launchCapability: "audit.event.read",
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "taskschd",
    title: "Task Scheduler",
    kind: "task-scheduler",
    category: "admin",
    icon: "calendar-clock",
    runAliases: ["taskschd.msc", "task scheduler", "scheduled tasks"],
    keywords: ["schedule", "task", "trigger"],
    profileIds: ["workstation", "server-2025"],
    launchCapability: "task.scheduler.read",
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "taskmgr",
    title: "Task Manager",
    kind: "task-manager",
    category: "admin",
    icon: "activity",
    runAliases: ["taskmgr", "task manager"],
    keywords: ["process", "performance", "startup"],
    profileIds: sharedUtilityProfiles,
    defaultSize: { width: 780, height: 560 }
  },
  {
    id: "wf",
    title: "Firewall Console",
    kind: "firewall-console",
    category: "security",
    icon: "shield",
    runAliases: ["wf.msc", "firewall"],
    keywords: ["firewall", "profile", "rule"],
    profileIds: ["workstation", "server-2025"],
    launchCapability: "firewall.profile.read",
    defaultSize: { width: 900, height: 600 }
  },
  {
    id: "settingsNetwork",
    title: "Network Settings",
    kind: "settings",
    category: "network",
    icon: "network",
    runAliases: ["network settings", "ms-settings:network"],
    keywords: ["network", "dns", "ip", "adapter"],
    profileIds: sharedUtilityProfiles,
    defaultSize: { width: 920, height: 620 }
  },
  {
    id: "snippingTool",
    title: "FD Snipping Tool",
    kind: "generic",
    category: "productivity",
    icon: "scissors",
    runAliases: ["snippingtool", "snipping tool", "screenshot"],
    keywords: ["screenshot", "evidence", "capture"],
    profileIds: ["workstation", "client-cloud-admin"],
    defaultSize: { width: 640, height: 420 }
  },
  {
    id: "clipboardHistory",
    title: "Clipboard History",
    kind: "generic",
    category: "productivity",
    icon: "clipboard",
    runAliases: ["clipboard", "clipboard history"],
    keywords: ["clipboard", "copy", "paste"],
    profileIds: ["workstation", "client-cloud-admin"],
    defaultSize: { width: 520, height: 520 }
  },
  {
    id: "entraUsers",
    title: "Cloud Users",
    kind: "cloud-admin",
    category: "admin",
    icon: "cloud-users",
    runAliases: ["cloud users", "cloud directory users", "users"],
    keywords: ["cloud", "users", "accounts"],
    profileIds: cloudProfiles,
    desktop: true,
    launchCapability: "cloud.users.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "entraGroups",
    title: "Cloud Groups",
    kind: "cloud-admin",
    category: "admin",
    icon: "cloud-groups",
    runAliases: ["cloud groups", "cloud directory groups", "groups"],
    keywords: ["cloud", "groups", "membership"],
    profileIds: cloudProfiles,
    launchCapability: "cloud.groups.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "entraDevices",
    title: "Cloud Devices",
    kind: "cloud-admin",
    category: "admin",
    icon: "devices",
    runAliases: ["cloud devices", "devices"],
    keywords: ["cloud", "devices", "compliance"],
    profileIds: cloudProfiles,
    launchCapability: "cloud.devices.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "m365Licenses",
    title: "Cloud Licenses",
    kind: "cloud-admin",
    category: "admin",
    icon: "badge",
    runAliases: ["cloud licenses", "licenses"],
    keywords: ["licenses", "cloud", "assignment"],
    profileIds: cloudProfiles,
    launchCapability: "m365.licenses.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "entraRoles",
    title: "Cloud Roles",
    kind: "cloud-admin",
    category: "security",
    icon: "badge",
    runAliases: ["cloud roles", "roles", "admin roles"],
    keywords: ["cloud", "roles", "assignments"],
    profileIds: cloudProfiles,
    launchCapability: "m365.roles.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "conditionalAccess",
    title: "Access Policies",
    kind: "cloud-admin",
    category: "security",
    icon: "shield",
    runAliases: ["access policies", "cloud access", "policies"],
    keywords: ["cloud", "access", "policy"],
    profileIds: cloudProfiles,
    launchCapability: "m365.access.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "authMethods",
    title: "Authentication Methods",
    kind: "cloud-admin",
    category: "security",
    icon: "shield",
    runAliases: ["authentication methods", "auth methods", "mfa"],
    keywords: ["cloud", "auth", "mfa"],
    profileIds: cloudProfiles,
    launchCapability: "m365.authentication.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "enterpriseApps",
    title: "Enterprise Applications",
    kind: "cloud-admin",
    category: "admin",
    icon: "cloud",
    runAliases: ["enterprise apps", "enterprise applications", "cloud apps"],
    keywords: ["cloud", "applications", "consent"],
    profileIds: cloudProfiles,
    launchCapability: "m365.apps.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "intuneDevices",
    title: "Managed Devices",
    kind: "cloud-admin",
    category: "admin",
    icon: "devices",
    runAliases: ["managed devices", "device management"],
    keywords: ["cloud", "devices", "management"],
    profileIds: cloudProfiles,
    launchCapability: "m365.intune.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "compliancePolicies",
    title: "Compliance Policies",
    kind: "cloud-admin",
    category: "security",
    icon: "shield",
    runAliases: ["compliance policies", "compliance"],
    keywords: ["cloud", "compliance", "policy"],
    profileIds: cloudProfiles,
    launchCapability: "m365.compliance.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "configurationProfiles",
    title: "Configuration Profiles",
    kind: "cloud-admin",
    category: "admin",
    icon: "settings",
    runAliases: ["configuration profiles", "config profiles"],
    keywords: ["cloud", "configuration", "profile"],
    profileIds: cloudProfiles,
    launchCapability: "m365.intune.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "m365Users",
    title: "Cloud Licensed Users",
    kind: "cloud-admin",
    category: "admin",
    icon: "cloud-users",
    runAliases: ["licensed users", "cloud licensed users"],
    keywords: ["cloud", "users", "licenses"],
    profileIds: cloudProfiles,
    launchCapability: "m365.users.launch",
    defaultSize: { width: 980, height: 640 }
  },
  {
    id: "defenderPortal",
    title: "Security Portal",
    kind: "cloud-admin",
    category: "security",
    icon: "shield",
    runAliases: ["security portal", "cloud security", "incidents"],
    keywords: ["cloud", "security", "incident"],
    profileIds: cloudProfiles,
    launchCapability: "m365.security.launch",
    defaultSize: { width: 980, height: 640 }
  }
];

const fallbackProfiles: SimulatorProfileId[] = ["workstation", "server-2025", "client-cloud-admin"];

function definitionFromStudentApp(app: StudentSimulatorAppView): SimulatorAppDefinition {
  const title = sanitizeSimulatorAppTitle(app.name || app.appId);
  const categoryDefaults: Record<StudentSimulatorAppView["category"], SimulatorProfileId[]> = {
    admin: fallbackProfiles,
    desktop: fallbackProfiles,
    network: ["workstation", "server-2025"],
    productivity: fallbackProfiles,
    security: fallbackProfiles,
    server: serverProfiles
  };

  return {
    id: app.appId,
    title,
    kind: "generic",
    category: app.category,
    icon: app.category,
    runAliases: [app.appId, title],
    keywords: [app.appId, title, app.category],
    profileIds: categoryDefaults[app.category],
    defaultSize: { width: 860, height: 580 }
  };
}

export function createSimulatorRegistry(studentApps: StudentSimulatorAppView[] = []): SimulatorAppDefinition[] {
  const byId = new Map<string, SimulatorAppDefinition>();
  for (const app of baseSimulatorApps) {
    byId.set(app.id, app);
  }
  for (const app of studentApps) {
    if (!byId.has(app.appId)) {
      byId.set(app.appId, definitionFromStudentApp(app));
    }
  }
  return [...byId.values()].sort((left, right) => left.title.localeCompare(right.title, "nl-NL"));
}

export function appsForProfile(registry: SimulatorAppDefinition[], profileId: SimulatorProfileId): SimulatorAppDefinition[] {
  return registry.filter((app) => app.profileIds.includes(profileId));
}

export function findAppById(registry: SimulatorAppDefinition[], appId: string): SimulatorAppDefinition | null {
  return registry.find((app) => app.id === appId) ?? null;
}

export function resolveRunAlias(registry: SimulatorAppDefinition[], rawCommand: string): SimulatorAppDefinition | null {
  const command = rawCommand.trim().toLowerCase();
  if (!command) return null;
  return registry.find((app) => app.runAliases.some((alias) => alias.toLowerCase() === command))
    ?? registry.find((app) => app.runAliases.some((alias) => command.startsWith(`${alias.toLowerCase()} `)))
    ?? null;
}

export function profileForMachine(activeMachine: string): SimulatorProfileId {
  return activeMachine.trim().toUpperCase().startsWith("SRV") ? "server-2025" : "workstation";
}
